import React, {useCallback, useEffect} from "react";
import { Row, Col, Card, CardHeader, CardBody } from 'reactstrap';
import "react-toastify/dist/ReactToastify.css";
import Button from "react-bootstrap/Button";
import RightModal from "./modalForm";

// hooks
import { useRedux, useHistory, useFareRestriction, useSearchInput, useGroupSelectedItems } from "../../../hooks";

import {
    editDataSaleCustomer,
    getSaleCustomers,
    onRemoveSaleCustomer,
    removeSaleCustomerModalHide,
    removeSaleCustomerModalShow,
    showRightModalSaleCustomer
} from "../../../redux/actions";

import TableList from "../../../components/TableList";
import SaleCustomerColumns from "./tableColumns";
import {CustomerType} from "../../../redux/Sale/Customer/types";
import {EntityActionsHandlersType} from "../../../redux/types";
import RemoveModal from "../../../components/RemoveForm";

import '../../../i18/config';
import {useTranslation} from "react-i18next";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import Loader from "../../../components/Loader";
import {LicenseProductParam} from "../../../redux/Sale/License/types";
import UpgradeFarePage from "../../../components/UpgradeFarePage";
import MainListFilter from "../../../components/MainListFilter";

const SaleCustomerIndex = () => {
    const { dispatch, useAppSelector } = useRedux();

    const { t } = useTranslation(['common']);

    const componentType = 'saleCustomerTable';

    const {
        historyHash,
        searchPhrase,
        loadItems,
        handleSearchClick
    } = useHistory({
        componentType,
        loadAction: getSaleCustomers,
        dispatch
    });

    const { onSearchInput } = useSearchInput(handleSearchClick);

    const {
        saleCustomer,
        isFetched,
        salePlan
    } = useAppSelector(state => ({
        saleCustomer: state.SaleCustomer,
        isFetched: state.SaleCustomer.isFetched,
        salePlan: state.SalePlan
    }));

    const { upgradeFare } = useFareRestriction(
        salePlan?.currentPlan,
        LicenseProductParam.SALE_MAX_PRODUCTS
    );

    const {
        selectedItems,
        handleGroupActionSelectItems
    } = useGroupSelectedItems<CustomerType>();

    const openRightModal = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(editDataSaleCustomer({data:event, modalEditTitle: 'changeCustomer'}));
        } else {
            dispatch(showRightModalSaleCustomer({data:[], modalEditTitle: 'newCustomer'}));
        }
    },[dispatch]);

    const onEntityRemoveModalShow = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(removeSaleCustomerModalShow({data:event, modalEditTitle: 'deleteCustomer'}));
        }
    },[dispatch]);

    const onEntityRemoveModalHide = useCallback(() => dispatch(removeSaleCustomerModalHide()),[dispatch]);

    const onEntityRemove = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(onRemoveSaleCustomer({data:event, modalEditTitle: 'deleteCustomer'}));
        }
    },[dispatch]);

    useEffect(() => {
        if (saleCustomer.isNeedReload){
            loadItems();
        }
    }, [loadItems, saleCustomer.isNeedReload]);

    const EntityActionsHandlers:EntityActionsHandlersType = {
        onEdit: openRightModal,
        onRemove: onEntityRemoveModalShow
    }

    const onTableRowClick = useCallback((item:CustomerType) => {
        if (item.actions && item.actions.canEdit){
            openRightModal(item);
        }
    },[openRightModal]);

    const columns = SaleCustomerColumns(EntityActionsHandlers);

    return (
        <>
            <BreadCrumb title={t('menu:saleCustomers')} pageTitle={t('menu:main')} />
            { upgradeFare && isFetched ?
                <>
                    <div className="d-flex flex-row flex-grow-1">
                        <div className="alert alert-danger alert-border-left alert-dismissible fade show d-flex flex-row flex-grow-1" role="alert">
                            <i className="ri-error-warning-line me-2 align-middle"></i> <strong className="pe-2">{t('common:attention')}!</strong>{t('plan:restrictionSalesModuleText')}
                        </div>
                    </div>
                    <UpgradeFarePage/>
                </>
                :
                <>
                <Row className="g-4 mb-3">
                    <div className="col-sm-auto">
                        <div>
                            <Button variant="secondary" onClick={openRightModal} className="btn btn-success">
                                <i className="ri-add-line align-bottom me-1"/> {t('common:addNew')}
                            </Button>
                        </div>
                    </div>
                </Row>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-bottom-0">
                                <h5 className="card-title mb-0">{t('menu:saleCustomers')}</h5>
                            </CardHeader>
                            <CardBody className="border border-dashed border-end-0 border-start-0 pb-3">
                                <MainListFilter
                                    onClick={handleSearchClick}
                                    value={searchPhrase}
                                    onChange={onSearchInput}
                                />
                            </CardBody>
                            <CardBody>
                                {isFetched ?
                                    <TableList
                                    columns={columns}
                                    data={saleCustomer.items}
                                    onRowClick={onTableRowClick}
                                    key={componentType}
                                    historyHash = {historyHash}
                                    selectableRows = {true}
                                    handleGroupActionSelectItems = {handleGroupActionSelectItems}
                                    paginationTotalRows={saleCustomer?.meta?.totalCount || 0}
                                    paginationServer={true}
                                    isShowChangePageSize={true}
                                    />
                                    :
                                    <Loader/>
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <RightModal/>
                <RemoveModal
                    onRemove={onEntityRemove}
                    handleClose={onEntityRemoveModalHide}
                    removeItem={saleCustomer.selectedItem}
                    isOpen={saleCustomer.removeModalShow}
                    modalTitle={saleCustomer.modalEditTitle}/>
                </>
            }
        </>
    );
};

export default SaleCustomerIndex;


